import React, { useState } from 'react';
import { X, Eye, EyeOff, KeyRound } from 'lucide-react';
import { api } from '../../api/client';

const ChangePasswordModal = ({ user, onClose }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirmPassword.length > 0 && password !== confirmPassword;
  const canSave = password.length >= 8 && password === confirmPassword;

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    setError('');
    try {
      await api.adminChangePassword(user.id, password);
      alert(`Password updated for ${user.email}`);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#01006A]" />
            Change Password
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Setting a new password for <span className="font-medium text-gray-900">{user.name}</span> ({user.email}).
            They'll need to use this new password next time they log in.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
                placeholder="At least 8 characters"
                autoFocus
              />
              <button onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {tooShort && <p className="text-xs text-amber-600 mt-1">Must be at least 8 characters.</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && canSave && handleSave()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
              placeholder="Re-enter the password"
            />
            {mismatch && <p className="text-xs text-red-600 mt-1">Passwords don't match.</p>}
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex gap-3 pt-2">
            <button onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">
              Cancel
            </button>
            <button onClick={handleSave} disabled={!canSave || isSaving}
              className="flex-1 px-4 py-2 bg-[#01006A] text-white rounded-lg hover:bg-[#010059] disabled:bg-gray-300 text-sm font-medium">
              {isSaving ? 'Saving…' : 'Save Password'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
